/**
 * `/sign-in/link`. Land from the email universal link.
 *
 * Web clients redeem in the browser. A phone client is offered a store only
 * when that store's link is set. Otherwise the browser finishes the sign in.
 * Already signed in still redeems and replaces the session (R8).
 *
 * File is `sign-in_.link.tsx` so this route is NOT a child of `/sign-in`.
 * That screen redirects signed-in fans away, which would skip redeem.
 */
import { useOtpRedeem } from "@sportsmet/auth-sdk/public";
import { useAppAvailabilityGet } from "@sportsmet/site-sdk/public";
import { Button, Card } from "@sportsmet/ui";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { showApiError } from "../errors/apiErrorBus";
import { coerceEnum, coerceOptionalString } from "../routeState/searchParams";
import { safeRedirectPath } from "./sign-in";

const SIGN_IN_CLIENTS = ["ios", "android", "web", "unknown"] as const;
export type typeSignInLinkClient = (typeof SIGN_IN_CLIENTS)[number];

export type typeSignInLinkSearch = {
  token?: string | undefined;
  client: typeSignInLinkClient;
  redirect?: string | undefined;
};

const REDIRECT_NAVIGATION = { reloadDocument: false } as const;

type typeLinkStatus = "idle" | "redeeming" | "failed" | "stopped" | "done";

const SignInLinkScreen = (): ReactNode => {
  const search = useSearch({ from: "/sign-in_/link" });
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const redeem = useOtpRedeem();
  const started = useRef(false);
  const [status, setStatus] = useState<typeLinkStatus>("idle");

  const token = search.token;
  const client = search.client;
  const availability = useAppAvailabilityGet({}, { enabled: client !== "web" });
  const availabilityData = availability.data?.data;
  const iosStoreUrl =
    typeof availabilityData?.iosStoreUrl === "string" &&
    availabilityData.iosStoreUrl !== ""
      ? availabilityData.iosStoreUrl
      : null;
  const androidStoreUrl =
    typeof availabilityData?.androidStoreUrl === "string" &&
    availabilityData.androidStoreUrl !== ""
      ? availabilityData.androidStoreUrl
      : null;
  const isAvailabilityResolved = client === "web" || !availability.isPending;
  const showIosStore =
    (client === "ios" || client === "unknown") && iosStoreUrl !== null;
  const showAndroidStore =
    (client === "android" || client === "unknown") && androidStoreUrl !== null;
  const doRedeemInBrowser =
    client === "web" ||
    (isAvailabilityResolved && !showIosStore && !showAndroidStore);

  useEffect(() => {
    if (!doRedeemInBrowser) return;
    if (token === undefined || token === "") {
      setStatus("failed");
      return;
    }
    if (started.current) return;
    started.current = true;
    setStatus("redeeming");
    redeem.mutate(
      { token },
      {
        onSuccess: (result) => {
          const errorCode = result.error?.code;
          if (
            errorCode === "ACCOUNT_SUSPENDED" ||
            errorCode === "ACCOUNT_BANNED"
          ) {
            showApiError(result, "accountState");
            setStatus("stopped");
            return;
          }
          const session = result.data;
          if (result.error !== null || !session) {
            setStatus("failed");
            return;
          }
          signIn({
            accessToken: session.accessToken,
            refreshToken: session.refreshToken,
          });
          setStatus("done");
          void navigate({
            href: safeRedirectPath(search.redirect),
            ...REDIRECT_NAVIGATION,
          });
        },
        onError: () => {
          setStatus("failed");
        },
      },
    );
  }, [doRedeemInBrowser, navigate, redeem, search.redirect, signIn, token]);

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-4 py-6 sm-screen-bottom">
      <div className="flex flex-col gap-1">
        <h1 className="text-h1 text-ink">Sign in</h1>
        <p className="text-body text-ink-secondary">
          {status === "redeeming"
            ? "Signing you in…"
            : status === "stopped"
              ? "We can't sign you in."
              : status === "failed"
                ? "That sign-in link is no longer valid. Ask for a new code."
                : !isAvailabilityResolved
                  ? "Signing you in…"
                  : doRedeemInBrowser
                    ? "Opening your Sportsmet account."
                    : "Get the Sportsmet app to finish signing in."}
        </p>
      </div>

      <Card>
        <div className="flex flex-col gap-4">
          {status === "failed" ? (
            <Button asChild variant="primary" fullWidth>
              <Link to="/sign-in" search={{ step: "email" }}>
                Ask for a new code
              </Link>
            </Button>
          ) : null}

          {!doRedeemInBrowser && status !== "failed" ? (
            <>
              {showIosStore ? (
                <Button asChild variant="primary" fullWidth>
                  <a href={iosStoreUrl} rel="noopener noreferrer">
                    Get it on the App Store
                  </a>
                </Button>
              ) : null}
              {showAndroidStore ? (
                <Button asChild variant="primary" fullWidth>
                  <a href={androidStoreUrl} rel="noopener noreferrer">
                    Get it on Google Play
                  </a>
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </Card>
    </div>
  );
};

export function coerceSignInLinkSearch(
  search: Record<string, unknown>,
): typeSignInLinkSearch {
  return {
    token: coerceOptionalString(search.token),
    client: coerceEnum(search.client, SIGN_IN_CLIENTS, "unknown"),
    redirect: coerceOptionalString(search.redirect),
  };
}

export const Route = createFileRoute("/sign-in_/link")({
  validateSearch: coerceSignInLinkSearch,
  component: SignInLinkScreen,
});
